import styled, { css, keyframes } from 'styled-components'
import ReactModal from 'react-modal'
import { SlideProps, Slider } from 'pure-react-carousel'
import { tabletBreakpoint, mobileBreakpoint, desktopBreakpoint } from 'react-components'

export const loadingShimmer = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`

export const StyledSlider = styled(Slider)<SlideProps>`
    position: relative;
`

export const ImageOuterContainer = styled.div(
    ({ theme }) => css`
        display: grid;
        width: 100%;
        grid-template-columns: repeat(3, minmax(10rem, 1fr));
        gap: ${theme.spacing.medium};
        padding-left: 10%;
        padding-right: 10%;
        grid-auto-rows: min-content;
        padding-top: ${theme.spacing.medium};
        padding-bottom: ${theme.spacing.medium};

        @media only screen and (max-width: ${tabletBreakpoint.max}px) {
            padding-left: 8%;
            padding-right: 8%;
            grid-template-columns: repeat(2, minmax(7.5rem, 1fr));
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            grid-template-columns: repeat(2, minmax(6.25rem, 1fr));
            padding-left: 0;
            padding-right: 0;
        }
    `,
)

export const ScrollableContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
`

export const ImageInnerContainer = styled.div(
    ({ theme }) => css`
        width: 100%;
        aspect-ratio: 4 / 3;
        background-color: ${theme.colors.tfwBlack12};
        border-radius: ${theme.typography.border.radius.medium};

        @media (max-width: 768px) {
            &:nth-of-type(3n + 1) {
                grid-column: span 2;
            }

            &:nth-of-type(3n + 2) {
                grid-column: 1;
            }

            &:nth-of-type(3n + 3) {
                grid-column: 2;
            }
        }

        img {
            display: flex;
            flex: 1;
            height: 100%;
            width: 100%;
            border-radius: ${theme.typography.border.radius.medium};
            transition: filter 0.3s ease;

            @media only screen and (max-width: ${mobileBreakpoint.max}px) {
                border-radius: ${theme.typography.border.radius.none};
            }

            @media only screen and (min-width: ${desktopBreakpoint.min}px) {
                &:hover {
                    filter: brightness(0.8);
                }
            }
        }
    `,
)
export const StickyHeaderContainer = styled.div(
    ({ theme }) => css`
        position: ${theme.style.position.sticky};
        top: 0;
        z-index: 99999;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        background-color: ${theme.colors.white};
    `,
)

export const StickyFooterContainer = styled.div(
    ({ theme }) => css`
        position: ${theme.style.position.sticky};
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 99999;
        box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
        background-color: ${theme.colors.white};
    `,
)

export const StyledImageContainer = styled.div<{ imageBorderRadius?: string; imageBorder?: string }>(
    ({ imageBorderRadius, imageBorder, theme }) => css`
        &:focus,
        &:focus-visible {
            outline: none;
        }
        background-color: ${theme.colors.tfwBlack12};
        cursor: pointer;
        ${!!imageBorderRadius &&
        css`
            border-radius: ${imageBorderRadius};
        `};
        ${!!imageBorder &&
        css`
            border: ${imageBorderRadius};
        `};
    `,
)

export const StyledImage = styled.img.withConfig({
    shouldForwardProp: prop => !['imageBorderRadius', 'imageBorder'].includes(prop),
})<{ imageBorderRadius?: string; imageBorder?: string }>`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    cursor: pointer;
    &:focus {
        outline: none;
    }
    ${({ imageBorderRadius }) =>
        imageBorderRadius &&
        css`
            border-radius: ${imageBorderRadius};
        `};
    ${({ imageBorder }) =>
        imageBorder &&
        css`
            border: ${imageBorder};
        `};
`

export const OverlayStyledImage = styled.img(
    ({ theme }) => css`
        display: flex;
        flex: 1;
        justify-content: ${theme.style.justifyContent.center};
        align-items: ${theme.style.alignItems.center};
        width: 100%;
        aspect-ratio: 4 / 3;
        max-width: 50rem;
        overflow: hidden;
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.tfwBlack12};
        cursor: pointer;
        &:focus {
            outline: none;
        }
    `,
)

export const StyledCarouselWrapper = styled.div(
    ({ theme }) => css`
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
        position: ${theme.style.position.relative};
        &:focus-visible {
            outline: none;
        }
    `,
)

export const ChevronWrapperBase = styled.div(
    ({ theme }) => css`
        display: none;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        transition: transform 0.3s ease, opacity 0.3s ease;

        &:hover {
            transform: translateY(-50%) scale(1.1);
            opacity: 0.8;
        }

        &:active {
            transform: translateY(-50%) scale(0.9);
            opacity: 0.6;
        }
        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            display: flex;
        }
    `,
)

export const LeftChevronWrapper = styled(ChevronWrapperBase)`
    left: 0;
`

export const RightChevronWrapper = styled(ChevronWrapperBase)`
    right: 0;
`

export const CloseIconWrapper = styled.div(
    ({ theme }) => css`
        position: absolute;
        top: ${theme.spacing.medium};
        right: ${theme.spacing.medium};
        cursor: pointer;
        transition: transform 0.3s ease, opacity 0.3s ease;

        &:hover {
            transform: scale(1.1);
            opacity: 0.8;
        }

        &:active {
            transform: scale(0.9);
            opacity: 0.6;
        }
    `,
)

export const StyledFigure = styled.figure(
    ({ theme }) => css`
        z-index: 10000;
        display: flex;
        flex-direction: ${theme.style.flexDirection.column};
        align-items: ${theme.style.alignItems.center};
        margin: ${theme.spacing.medium};
    `,
)

export const StyledFigcaption = styled.figcaption(
    ({ theme }) => css`
        z-index: 10000;
        margin-top: ${theme.spacing.medium};
    `,
)

export const Counter = styled.div(
    ({ theme }) => css`
        position: absolute;
        bottom: ${theme.spacing.medium};
        right: ${theme.spacing.medium};
        background-color: ${theme.colors.tfwBlack30};
        border-radius: ${theme.typography.border.radius.medium};
        font-family: ${theme.typography.font.family.inter};
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.weight.normal};
        padding: ${theme.spacing.xxSmall} ${theme.spacing.xSmall};
        color: ${theme.colors.white};
    `,
)

export const LeftArrowContainer = styled.div(
    ({ theme }) => css`
        position: absolute;
        left: ${theme.spacing.small};
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        flex: 1;
        cursor: pointer;
    `,
)

export const RightArrowContainer = styled.div(
    ({ theme }) => css`
        position: absolute;
        right: ${theme.spacing.small};
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        flex: 1;
        cursor: pointer;
    `,
)

export const BgPictureContainer = styled.picture`
    width: 100%;
    height: auto;
    cursor: pointer;
    position: relative;
`
export const GridContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        position: relative;
        gap: 0.625rem;
        height: auto;
        border-radius: ${theme.typography.border.radius.xLarge};
        overflow: hidden;
        min-width: 0;
    `,
)

export const MainImageContainer = styled.div<{ imageBorderRadius?: string; imageBorder?: string }>(
    ({ imageBorderRadius, imageBorder, theme }) => css`
        flex: 2 1 0;
        cursor: pointer;
        position: relative;
        border-radius: ${imageBorderRadius || '0.75rem'};
        border: ${imageBorder || 'none'};
        overflow: hidden;
        min-width: 0;
        background-color: ${theme.colors.tfwBlack12};
        aspect-ratio: 4 / 3;

        /* When third column is hidden (<=1024px), make main image half width */
        @media (max-width: ${desktopBreakpoint.min}px) {
            flex: 1 1 0;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            &:hover img {
                filter: brightness(0.8);
            }
        }
    `,
)

export const ThumbnailsGrid = styled.div`
    flex: 3 1 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.625rem;
    min-width: 0;

    @media (max-width: ${desktopBreakpoint.min}px) {
        flex: 1 1 0;
        grid-template-columns: repeat(2, 1fr);

        > *:nth-child(n + 5) {
            display: none;
        }
    }

    @media (max-width: 425px) {
        display: none;
    }
`

export const ThumbnailContainer = styled.div<{
    imageBorderRadius?: string
    imageBorder?: string
    isLast?: boolean
}>(
    ({ imageBorderRadius, imageBorder, theme }) => css`
        flex: 1 1 0;
        cursor: pointer;
        position: relative;
        border-radius: ${imageBorderRadius || '0.75rem'};
        border: ${imageBorder || 'none'};
        overflow: hidden;
        min-height: 0;
        background-color: ${theme.colors.tfwBlack12};
        aspect-ratio: 4 / 3;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            &:hover img {
                filter: brightness(0.8);
            }
        }
    `,
)

export const MainImage = styled(StyledImage)`
    width: 100%;
    height: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    transition: filter 0.3s ease;
`

export const ThumbnailImage = styled(StyledImage)`
    width: 100%;
    height: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    transition: filter 0.3s ease;
`

export const ViewAllOverlay = styled.div.withConfig({
    shouldForwardProp: prop => (prop as string) !== 'fontFamily',
})<{ fontFamily?: string }>(
    ({ theme, fontFamily }) => css`
        position: absolute;
        bottom: 0.5rem;
        right: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        background: rgba(255, 255, 255, 0.95);
        color: #000;
        font-family: ${fontFamily || theme.typography.font.family.inter};
        font-weight: 400;
        font-size: ${theme.typography.font.size.small};
        padding: 0.375rem 0.5rem;
        border-radius: ${theme.typography.border.radius.medium};
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;

        &:hover {
            background: rgba(255, 255, 255, 1);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
    `,
)

export const GalleryIcon = styled.img.attrs<{ $iconSrc?: string }>(({ $iconSrc }) => ({
    src: $iconSrc || '/icons/gallery-icon.svg',
    alt: 'Gallery',
}))<{ $iconSrc?: string }>`
    width: 16px;
    height: 16px;
    display: inline-block;
`

export const DotsContainer = styled.div(
    ({ theme }) => css`
        position: absolute;
        bottom: ${theme.spacing.small};
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: ${theme.spacing.xxxSmall};
    `,
)

export const Dot = styled.div<{ isActive: boolean }>(
    ({ isActive, theme }) => css`
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: ${isActive ? theme.colors.tfwBlack30 : theme.colors.white};
        transition: background-color 0.3s ease;
    `,
)

export const SectionTitle = styled.h3(
    ({ theme }) => css`
        margin: 0;
        padding: ${theme.spacing.medium} 10%;
        font-size: ${theme.typography.font.size.large};
        font-weight: ${theme.typography.font.weight.semiBold};
    `,
)

export const FullScreenVideoContainer = styled.div(
    ({ theme }) => css`
        grid-column: span 2;
        aspect-ratio: 16 / 9;
        border-radius: ${theme.typography.border.radius.medium};
        cursor: pointer;
    `,
)

export const OverlayVideoWrapper = styled.div(
    () => css`
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
    `,
)

export const OverlayFooterWrapper = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: ${theme.spacing.small};
        padding: ${theme.spacing.medium};
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;

        > div {
            display: flex !important;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
        }

        @media (max-width: ${desktopBreakpoint.min}px) {
            gap: ${theme.spacing.small};
            padding: 0;
            width: auto;
            justify-content: center;
        }

        > div > div {
            width: 40%;
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            flex-direction: column;

            > div {
                flex-direction: column;
                width: 100%;
            }

            > div > div {
                width: 100%;
            }
        }
    `,
)

export const fullScreenModalStyles: ReactModal.Styles = {
    content: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flexGrow: 1,
        padding: 0,
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        maxWidth: '100%',
        maxHeight: '100%',
        backgroundColor: 'white',
    },
}

export const overlayCarouselModalStyles: ReactModal.Styles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
    content: {
        position: 'unset',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: 0,
        border: 'none',
        borderRadius: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        maxWidth: 'min(90%, 800px)',
    },
}

export const VirtualTourWrapper = styled.div`
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
`
